/**
 * Global Error Handlers Hook
 * Catches unhandled promise rejections and global errors
 * Should be called once in the root layout/client component
 */

'use client';

import { useEffect } from 'react';

interface ErrorHandlerOptions {
  onError?: (error: ErrorEvent) => void;
  onUnhandledRejection?: (event: PromiseRejectionEvent) => void;
  logToConsole?: boolean;
  showToast?: boolean;
}

export function useGlobalErrorHandlers(options: ErrorHandlerOptions = {}) {
  const {
    onError,
    onUnhandledRejection,
    logToConsole = true,
    showToast = true,
  } = options;

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      
      if (logToConsole) {
        console.error('Unhandled promise rejection:', error);
      }

      // Optional: Send to error tracking
      if (typeof window !== 'undefined' && (window as any).Sentry) {
        (window as any).Sentry.captureException(error);
      }

      // Show user-friendly toast
      if (showToast && typeof window !== 'undefined') {
        // Try to use toast if available
        if ((window as any).toast?.error) {
          (window as any).toast.error(
            'Something failed. Your changes may not have saved. Please check your work.'
          );
        } else {
          // Fallback: console warning
          console.warn(
            '⚠️ Unhandled error occurred. Your changes may not have saved. Please check your work.'
          );
        }
      }

      // Call custom handler if provided
      if (onUnhandledRejection) {
        onUnhandledRejection(event);
      }

      // Prevent default browser error handling in dev
      if (process.env.NODE_ENV === 'development') {
        event.preventDefault();
      }
    };

    // Handle global JavaScript errors
    const handleError = (event: ErrorEvent) => {
      // Ignore errors from extensions, third-party scripts, etc.
      if (
        event.filename &&
        (event.filename.includes('chrome-extension://') ||
         event.filename.includes('moz-extension://') ||
         event.filename.includes('safari-extension://'))
      ) {
        return;
      }

      if (logToConsole) {
        console.error('Global error:', event.error || event.message);
      }

      // Optional: Send to error tracking
      if (typeof window !== 'undefined' && (window as any).Sentry && event.error) {
        (window as any).Sentry.captureException(event.error);
      }

      // Show user-friendly toast
      if (showToast && typeof window !== 'undefined') {
        if ((window as any).toast?.error) {
          (window as any).toast.error(
            'An error occurred. Please refresh the page if the issue persists.'
          );
        }
      }

      // Call custom handler if provided
      if (onError) {
        onError(event);
      }
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [onError, onUnhandledRejection, logToConsole, showToast]);
}
